/**
 * VShred Quiz Submission Module
 *
 * Collects quiz answers and sends them to the analytics microservice.
 * Optimized for minimal UI impact and maximum data collection:
 *   - fetch() with keepalive ensures requests survive most navigations
 *   - A pending-payload queue is flushed via navigator.sendBeacon() on
 *     page hide / unload as a last-resort safety net
 *   - Each answer is pushed to window.dataLayer (GTM) with event name
 *     "vsquiz_answer"; re-answering the same step overwrites in-place
 *   - Answers are persisted in localStorage under "vsquiz_{quiz_id}",
 *     keyed by question_step, so only the latest response is kept
 *
 * API URL must be set in one of these ways (no built-in default):
 *   - data-api-url on the script tag: <script src="vqzm.js" data-api-url="https://..."></script>
 *   - global variable: window.VQZM_API_URL = "https://...";
 *   - or pass api_url in the init config (overrides the above).
 *
 * Usage:
 *   1. Include this script in the <head> of a page.
 *   2. Initialize with configuration:
 *        quizSubmissionModule({
 *          quiz_id: "my_quiz",
 *          version_number: 1,
 *          quiz_name: "My Quiz",
 *          customer_id: "cust_123",
 *          split_id: 1,
 *          api_url: "https://..." // optional override
 *        });
 *   3. Submit answers:
 *        quizSubmissionModule.submitAnswer("What is your age?", "42", 1);
 */
const quizSubmissionModule = (function () {
  let _config = null;
  let _initialized = false;
  let _loggingEnabled = false;

  /**
   * Pending payloads that have not yet been confirmed by the server.
   * Each entry is the JSON string ready to send, keyed by a unique id.
   */
  const _pending = {};
  let _pendingId = 0;

  // ── Helpers ──────────────────────────────────────────────────────────

  /**
   * Resolve the default API URL: data-api-url on script tag, then
   * window.VQZM_API_URL. Returns undefined if neither is set.
   */
  function _getDefaultApiUrl() {
    if (typeof document !== 'undefined' && document.currentScript) {
      const dataUrl = document.currentScript.getAttribute('data-api-url');
      if (dataUrl) return dataUrl.trim();
    }
    if (typeof window !== 'undefined' && window.VQZM_API_URL != null) {
      const w = window.VQZM_API_URL;
      return typeof w === 'string' ? w.trim() : String(w);
    }
    return undefined;
  }

  function _log(level, message, data) {
    if (!_loggingEnabled) return;
    const prefix = '[vsquiz]';
    if (data !== undefined) {
      console[level](prefix, message, data);
    } else {
      console[level](prefix, message);
    }
  }

  function _getDomain() {
    try {
      return window.location.hostname;
    } catch (e) {
      return '';
    }
  }

  function _validateConfig(config) {
    if (!config || typeof config !== 'object') {
      throw new Error('quizSubmissionModule: config must be an object.');
    }

    const required = ['quiz_id', 'version_number', 'quiz_name', 'split_id'];
    for (let i = 0; i < required.length; i += 1) {
      if (config[required[i]] === undefined || config[required[i]] === null) {
        throw new Error(
          `quizSubmissionModule: missing required config property "${required[i]}".`
        );
      }
    }

    if (
      typeof config.version_number !== 'number' ||
      typeof config.split_id !== 'number'
    ) {
      throw new Error(
        'quizSubmissionModule: version_number and split_id must be numbers.'
      );
    }
  }

  function _validateAnswer(question, response, questionStep) {
    if (typeof question !== 'string' || question === '') {
      throw new Error(
        'quizSubmissionModule.submitAnswer: question must be a non-empty string.'
      );
    }
    if (response === undefined || response === null) {
      throw new Error(
        'quizSubmissionModule.submitAnswer: response is required.'
      );
    }
    if (typeof questionStep !== 'number') {
      throw new Error(
        'quizSubmissionModule.submitAnswer: question_step must be a number.'
      );
    }
  }

  // ── dataLayer ────────────────────────────────────────────────────────

  /**
   * Push (or overwrite) a dataLayer event for a given answer.
   * Events are identified by quiz_id + question_step so that re-answering
   * the same question replaces the previous entry in-place rather than
   * appending a duplicate.
   *
   * @param {Object} payload  The same payload object sent to the API.
   */
  function _pushDataLayer(payload) {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];

    // Use a step-specific key so each question occupies its own namespace
    // in GTM's internal data model.  Re-answering the same step pushes
    // the same key again, which GTM merges over the previous value.
    // Different steps use different keys and never collide.
    const stepKey = `vsquiz_step_${payload.question_step}`;

    const event = {
      event: 'vsquiz_answer',
      [stepKey]: Object.assign({}, payload),
    };

    // Also replace the matching array entry so the raw dataLayer array
    // stays tidy (one entry per quiz + step, not duplicates).
    let replaced = false;
    for (let i = 0; i < window.dataLayer.length; i += 1) {
      const entry = window.dataLayer[i];
      if (
        entry &&
        entry.event === 'vsquiz_answer' &&
        entry[stepKey] &&
        entry[stepKey].quiz_id === payload.quiz_id
      ) {
        window.dataLayer[i] = event;
        replaced = true;
        break;
      }
    }

    if (!replaced) {
      window.dataLayer.push(event);
    }

    _log('info', `dataLayer ${replaced ? 'updated' : 'pushed'} for step ${payload.question_step}.`, event);
  }

  // ── localStorage ────────────────────────────────────────────────────

  const STORAGE_PREFIX = 'vsquiz_';

  /**
   * Persist the latest answer for a question into localStorage, keyed by
   * quiz_id.  The stored structure is:
   *
   *   { [question_step]: { question, response, question_step, timestamp } }
   *
   * Re-answering the same step overwrites only that entry, preserving all
   * other answers for the quiz.
   *
   * @param {Object} payload  The same payload object sent to the API.
   */
  function _saveToLocalStorage(payload) {
    try {
      const key = STORAGE_PREFIX + payload.quiz_id;
      let stored = {};

      const raw = localStorage.getItem(key);
      if (raw) {
        stored = JSON.parse(raw);
      }

      stored[payload.question_step] = {
        question: payload.question,
        response: payload.response,
        question_step: payload.question_step,
        timestamp: Date.now(),
      };

      localStorage.setItem(key, JSON.stringify(stored));
      _log('info', `localStorage updated for step ${payload.question_step}.`, stored);
    } catch (e) {
      // localStorage may be full or unavailable (private browsing, etc.).
      _log('warn', 'Could not write to localStorage.', e);
    }
  }

  // ── Beacon flush (page lifecycle safety net) ─────────────────────────

  /**
   * Flush every pending payload via navigator.sendBeacon().
   * Called automatically when the page is being hidden or unloaded so that
   * data is not lost if the user navigates away before fetch completes.
   */
  function _flushPending() {
    const keys = Object.keys(_pending);
    if (keys.length === 0) return;

    const url = _config ? _config.api_url : _getDefaultApiUrl();
    if (!url) return;
    const hasSendBeacon =
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function';

    for (let i = 0; i < keys.length; i += 1) {
      const body = _pending[keys[i]];
      if (hasSendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        const queued = navigator.sendBeacon(url, blob);
        if (queued) {
          _log('info', `Beacon queued for pending payload ${keys[i]}.`);
          delete _pending[keys[i]];
        } else {
          _log('warn', `Beacon rejected for pending payload ${keys[i]}.`);
        }
      }
    }
  }

  /**
   * Register page lifecycle listeners once, on first init.
   * - "visibilitychange" fires when the tab is hidden (tab switch, minimize,
   *   navigation in most browsers). This is the recommended modern approach.
   * - "pagehide" fires on actual navigation / tab close and is compatible
   *   with the back-forward cache (bfcache).
   */
  let _listenersRegistered = false;

  function _registerLifecycleListeners() {
    if (_listenersRegistered) return;
    _listenersRegistered = true;

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') {
          _flushPending();
        }
      });
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', _flushPending);
    }
  }

  // ── Core ─────────────────────────────────────────────────────────────

  /**
   * Initialize the module with quiz-level configuration.
   *
   * @param {Object} config
   * @param {string}  config.quiz_id
   * @param {number}  config.version_number
   * @param {string}  config.quiz_name
   * @param {string}  [config.customer_id]
   * @param {number}  config.split_id
   * @param {string}  [config.api_url]         Override the default endpoint URL.
   * @param {boolean} [config.enable_logging]  Turn on console logging (default: false).
   */
  function init(config) {
    _validateConfig(config);

    _loggingEnabled = config.enable_logging === true;

    const apiUrl = config.api_url || _getDefaultApiUrl();
    if (!apiUrl) {
      throw new Error(
        'quizSubmissionModule: api_url is required. Set it via config.api_url, ' +
        'data-api-url on the script tag, or window.VQZM_API_URL.'
      );
    }
    _config = {
      api_url: apiUrl,
      quiz_id: config.quiz_id,
      version_number: config.version_number,
      quiz_name: config.quiz_name,
      customer_id: config.customer_id || '',
      split_id: config.split_id,
      domain: config.domain || _getDomain(),
    };

    _initialized = true;
    _registerLifecycleListeners();
    _log('info', 'Initialized.', _config);
  }

  /**
   * Submit a single quiz answer to the collection endpoint.
   *
   * The payload is added to a pending queue before the request is made.
   * If fetch succeeds the entry is removed; if the page is unloaded before
   * the response arrives, the pending queue is flushed via sendBeacon().
   *
   * @param {string} question       The question text.
   * @param {string} response       The user's response.
   * @param {number} questionStep   The step/sequence number of the question.
   * @returns {Promise}             Resolves with the parsed JSON response or
   *                                rejects with an Error.
   */
  function submitAnswer(question, response, questionStep) {
    if (!_initialized) {
      const err = new Error(
        'quizSubmissionModule.submitAnswer: module not initialized. ' +
        'Call quizSubmissionModule(config) first.'
      );
      _log('error', err.message);
      return Promise.reject(err);
    }

    _validateAnswer(question, response, questionStep);

    const payload = {
      quiz_id: _config.quiz_id,
      version_number: _config.version_number,
      domain: _config.domain,
      quiz_name: _config.quiz_name,
      question_step: questionStep,
      question,
      response: String(response),
      customer_id: _config.customer_id,
      split_id: _config.split_id,
    };

    // Persist locally before the network request so data is captured
    // even if the user navigates away immediately.
    _pushDataLayer(payload);
    _saveToLocalStorage(payload);

    const body = JSON.stringify(payload);

    // Track this payload until the server confirms receipt.
    _pendingId += 1;
    const id = _pendingId;
    _pending[id] = body;

    _log('info', `Submitting answer for step ${questionStep}…`, payload);

    return fetch(_config.api_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true, // request survives page navigation
    })
      .then(function (res) {
        // Confirmed by server – remove from pending queue.
        delete _pending[id];

        if (!res.ok) {
          throw new Error(
            `quizSubmissionModule: API responded with status ${res.status}`
          );
        }
        return res.json().catch(function () {
          return null;
        });
      })
      .then(function (data) {
        _log(
          'info',
          `Answer submitted successfully for step ${questionStep}.`,
          data
        );
        return data;
      })
      .catch(function (error) {
        // Leave payload in _pending so the lifecycle flush can retry it.
        _log(
          'error',
          `Failed to submit answer for step ${questionStep}.`,
          error
        );
        throw error;
      });
  }

  /**
   * Update the customer_id after initialization (e.g. once a user is
   * identified mid-quiz).
   *
   * @param {string} customerId
   */
  function setCustomerId(customerId) {
    if (!_initialized) {
      throw new Error(
        'quizSubmissionModule.setCustomerId: module not initialized.'
      );
    }
    _config.customer_id = String(customerId);
    _log('info', 'customer_id updated.', _config.customer_id);
  }

  // ── Public API ───────────────────────────────────────────────────────

  init.submitAnswer = submitAnswer;
  init.setCustomerId = setCustomerId;

  // Test-only reset (call after setting globalThis.__VQZM_TEST__ = true)
  if (typeof globalThis !== 'undefined' && globalThis.__VQZM_TEST__) {
    init.__reset = function () {
      _config = null;
      _initialized = false;
      _loggingEnabled = false;
      _listenersRegistered = false;
      Object.keys(_pending).forEach(function (k) {
        delete _pending[k];
      });
      _pendingId = 0;
    };
  }

  return init;
})();

// Expose for module environments (e.g. Jest require); browser script tag already gets global scope.
if (typeof globalThis !== 'undefined') {
  globalThis.quizSubmissionModule = quizSubmissionModule;
}
// Store reference for minifier
if (typeof window !== 'undefined') {
  window.quizSubmissionModule = quizSubmissionModule;
}

// Signal that the module is loaded and ready to be initialized.
if (typeof document !== 'undefined') {
  document.dispatchEvent(new CustomEvent('vsquiz:ready'));
}